import React from 'react';
import { useJazyk } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { MapPin, BadgeCheck, CalendarDays } from 'lucide-react';
import Flag from './Flag';

/**
 * Řádek s ukázkami obsahu – používá se na úvodní obrazovce Bloom World jak
 * pro domovskou zemi, tak pro věci ze světa.
 *
 * U obsahu ze světa se ukazuje i vlajka země, protože tam na tom záleží;
 * u domovské země by byla u každé karty stejná a jen by rušila.
 */

const datum = (iso, jazyk = 'cs') => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(jazyk === 'en' ? 'en-GB' : 'cs-CZ',
      { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function ContentRow({
  icon: Icon,
  title,
  polozky = [],
  kind,
  zeme,               // mapa iso2 -> země, pro vlajky u obsahu ze světa
  ukazatZemi = false,
  odkazVice,
  popisekVice,
}) {
  const { jazyk, t } = useJazyk();
  if (!polozky.length) return null;

  return (
    <section className="mt-7" data-testid={`content-row-${kind}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-bloom-text flex items-center gap-2">
          {Icon && <Icon className="w-4.5 h-4.5 text-bloom-violet" />}
          {title}
        </h3>
        {odkazVice && (
          <Link to={odkazVice} className="text-sm text-bloom-violet hover:underline shrink-0">
            {popisekVice || t('obecne.vice')}
          </Link>
        )}
      </div>

      <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {polozky.map((p) => {
          const zemeZaznamu = zeme?.[p.location?.country_iso2];
          const cil = kind === 'specialists'
            ? `/specialists?id=${p.id}`
            : `/world/${zemeZaznamu?.slug || p.location?.country_iso2?.toLowerCase()}/${kind}/${p.slug || p.id}`;

          return (
            <li key={p.id}>
              <Link to={cil}
                    className="block h-full bg-white border border-border/40 rounded-2xl p-3.5 hover:border-bloom-violet/40 hover:shadow-sm transition-all">
                <div className="flex items-start gap-2">
                  <p className="font-medium text-bloom-text text-sm flex-1 line-clamp-2">{p.name}</p>
                  {p.verification === 'verified' && (
                    <BadgeCheck className="w-4 h-4 text-bloom-mint shrink-0" />
                  )}
                </div>

                {p.specialty && <p className="mt-1 text-xs text-bloom-violet">{p.specialty}</p>}

                {p.details?.starts_at && (
                  <p className="mt-1.5 text-xs text-bloom-violet flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" />{datum(p.details.starts_at, jazyk)}
                  </p>
                )}

                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-bloom-sub">
                  {ukazatZemi && zemeZaznamu && (
                    <>
                      <Flag emoji={zemeZaznamu.flag} iso2={zemeZaznamu.iso2} size="sm" />
                      <span className="truncate">{zemeZaznamu.name_cs}</span>
                    </>
                  )}
                  {!ukazatZemi && p.location?.city_name && (
                    <>
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{p.location.city_name}</span>
                    </>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
